import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { engagementApi } from '../services/api';
import { BySource, DailyEngagement } from '../types';
import { istToday } from '../lib';
import DateRangeBar from '../components/DateRangeBar';
import EngagementFunnelStrip from '../components/EngagementFunnelStrip';
import EngagementTable from '../components/EngagementTable';
import SourcesCard from '../components/SourcesCard';
import { Section, Loading, PageFrame, Strip, Tabs, btn, btnTone } from '../components/PlatformBits';

/** Portal-wide link engagement over an inclusive IST day range — every
 *  source: campaign broadcasts, agent welcome-flow links, manual sends.
 *  The dates are just query params; the server owns the IST windowing. */
const SORT_OPTIONS: [string, string][] = [
  ['recent', 'Most recent'],
  ['opens', 'Most opens'],
  ['frames', 'Most frames'],
  ['dwell', 'Longest dwell'],
  ['load', 'Slowest load'],
  ['likes', 'Most likes'],
  ['shares', 'Most shares'],
  ['brochure', 'Brochure opens'],
];

// The surface a link belongs to, as the URL names it (?page=) → the link's
// form_type in core. The album and the website share the event stream but
// not the columns, so the table shrinks to what the chosen surface records.
const PAGES: [string, string, string][] = [
  ['all', '', 'both'],
  ['album', 'project_album', 'album'],
  ['website', 'website', 'website'],
];

const EngagementPage: React.FC = () => {
  // The source filter, page filter and date range live in the URL, so a
  // Dashboard row click ("show me this source's people") is just a link here.
  const [searchParams, setSearchParams] = useSearchParams();
  const [from, setFrom] = useState<string>(searchParams.get('from') || istToday());
  const [to, setTo] = useState<string>(searchParams.get('to') || istToday());
  const source = searchParams.get('source');
  const page = searchParams.get('page') || 'all';
  const formType = PAGES.find(([key]) => key === page)?.[1] || null;
  // Visitors who later messaged us and became leads — core owns the definition.
  const converted = searchParams.get('converted') === '1';
  const setParams = (next: { source?: string | null; page?: string; converted?: boolean }) => {
    const src = next.source === undefined ? source : next.source;
    const pg = next.page === undefined ? page : next.page;
    const cv = next.converted === undefined ? converted : next.converted;
    const q: Record<string, string> = {};
    if (src) q.source = src;
    if (pg !== 'all') q.page = pg;
    if (cv) q.converted = '1';
    if (src || pg !== 'all' || cv) { q.from = from; q.to = to; }
    setSearchParams(q, { replace: true });
  };
  const setSource = (tag: string | null) => setParams({ source: tag });
  const [sort, setSort] = useState<string>('recent');
  const [data, setData] = useState<DailyEngagement | null>(null);
  const [bySource, setBySource] = useState<BySource | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const PAGE_SIZE = 50;

  const load = useCallback(async (f: string, t: string, s: string, src: string | null, ft: string | null, cv: boolean, offset = 0) => {
    if (offset === 0) setData(null); else setLoadingMore(true);
    setError(null);
    try {
      const batch = await engagementApi.daily(f, t, PAGE_SIZE, offset, s, src, ft, cv);
      setData(prev => (offset > 0 && prev)
        ? { ...batch, links: [...prev.links, ...batch.links] }
        : batch);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => { load(from, to, sort, source, formType, converted); }, [from, to, sort, source, formType, converted, load]);

  // The per-source rollup is a window fact, independent of sort/pagination.
  useEffect(() => {
    setBySource(null);
    engagementApi.bySource(from, to)
      .then(setBySource)
      .catch((err: any) => setError(err?.response?.data?.detail || err.message));
  }, [from, to]);

  const rangeLabel = (d: DailyEngagement) =>
    d.date === d.date_to ? d.date : `${d.date} → ${d.date_to}`;

  const bar = (
    <>
      <DateRangeBar from={from} to={to} onChange={(f, t) => { setFrom(f); setTo(t); }} />
      {source && (
        <div className="flex items-center gap-2 -mt-4 mb-8">
          <span className="text-graphite text-[12.5px]">Showing only</span>
          <button
            onClick={() => setSource(null)}
            title="clear the source filter"
            className="inline-flex items-center gap-1.5 h-7 px-3 rounded-full text-forest bg-sage/30 text-[12.5px] font-semibold hover:bg-sage/50 transition-colors"
          >
            {bySource?.sources.find((s) => s.tag === source)?.source_label ?? source}
            <span aria-hidden>✕</span>
          </button>
        </div>
      )}
    </>
  );

  return (
    <PageFrame title="Engagement" bar={bar}>
      {error && <div className="rounded-lg bg-brick/5 text-brick px-4 py-3 text-[13.5px]">Couldn't load engagement: {error}</div>}
      {!error && !data && <Loading what="engagement" />}

      {data && data.funnel.links === 0 && (
        <p className="text-graphite text-[13.5px] py-2">
          No link activity on {rangeLabel(data)}{source ? ' from this source' : ''}{page !== 'all' ? ` on the ${page}` : ''}{converted ? ' by visitors who became leads' : ''}.
        </p>
      )}

      {data && data.funnel.links > 0 && (
        <>
          <Strip><EngagementFunnelStrip funnel={data.funnel} /></Strip>

          {bySource && bySource.sources.length > 0 && (
            <SourcesCard sources={bySource.sources} activeTag={source} onSelect={setSource} />
          )}

          <Section title="Links" note={
            <span className="inline-flex items-center gap-2 flex-wrap">
              <Tabs value={page} onChange={(k) => setParams({ page: k })} items={PAGES.map(([key, , label]) => [key, label])} />
              <button
                onClick={() => setParams({ converted: !converted })}
                aria-pressed={converted}
                title="only visitors who later messaged us on WhatsApp and became leads"
                className={`h-[30px] px-3 rounded-lg text-[12.5px] font-semibold transition-colors ${
                  converted ? 'bg-forest text-warm-white hover:bg-forest/90' : 'bg-charcoal/[0.06] text-graphite hover:text-charcoal'}`}
              >
                {converted ? '✓ ' : ''}Became leads
              </button>
              <label className="inline-flex items-center gap-1.5 ml-1 text-graphite text-[12.5px]">
                Sort
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  className="h-[30px] px-2 rounded-lg border border-charcoal/15 bg-warm-white text-[12.5px] font-semibold text-charcoal focus:outline-none focus:border-forest focus:ring-2 focus:ring-sage/40"
                >
                  {SORT_OPTIONS.map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </label>
            </span>
          }>
            <EngagementTable links={data.links} showSource albumColumns={page !== 'website'} />
            <div className="flex items-center justify-between gap-3 px-3 pt-4 text-[12px] text-graphite">
              <span>{data.links.length.toLocaleString('en-IN')} of {data.total_links.toLocaleString('en-IN')} links</span>
              {data.links.length < data.total_links && (
                <button
                  onClick={() => load(from, to, sort, source, formType, converted, data.links.length)}
                  disabled={loadingMore}
                  className={`${btn} ${btnTone.charcoal}`}
                >
                  {loadingMore ? 'Loading…' : `Load ${Math.min(PAGE_SIZE, data.total_links - data.links.length)} more`}
                </button>
              )}
            </div>
          </Section>
        </>
      )}
    </PageFrame>
  );
};

export default EngagementPage;
